import { CartItem, CartItemDetails, CartItemImpl } from '@/modules/store/models/CartItem';
import { DeliveryWindow, DeliveryWindowImpl } from '@/modules/store/models/DeliveryWindow';
import { PickupWindow, PickupWindowImpl } from '@/modules/store/models/PickupWindow';
import { CalculatedPrice } from '@/modules/store/models/Price';
import { Supplier } from '@/modules/store/models/Supplier';

export interface Cart {
  supplierId: string;
  items: CartItem[];
  moq: number;
  predictionId?: string;
  deliveryWindow?: DeliveryWindow;
  pickupWindow?: PickupWindow;
  totalQuantity: number;

  addItem(item: CartItem | CartItem[]): void;
  withItems(item: CartItem | CartItem[]): Cart;
}

type CartWithoutMethods = Omit<Cart, 'addItem' | 'withItems'>;

export interface CartDetails extends CartWithoutMethods {
  supplierName: string;
  currencyCode: string;
  items: CartItemDetails[];
  moq: number;
  predictionId?: string;
  deliveryWindow?: DeliveryWindow;
  pickupWindow?: PickupWindow;
  subtotal: CalculatedPrice;
  grandTotal: CalculatedPrice;
  totalQuantity: number;
}

export class CartImpl implements Cart {
  readonly supplierId: string;
  readonly moq: number;
  readonly items: CartItem[];
  readonly deliveryWindow?: DeliveryWindow;
  readonly pickupWindow?: PickupWindow;
  predictionId?: string | undefined;
  totalQuantity: number;

  constructor(
    supplierId: string,
    item?: CartItem | CartItem[],
    moq?: number,
    deliveryWindow?: DeliveryWindow,
    pickupWindow?: PickupWindow,
  ) {
    this.supplierId = supplierId;
    this.items = [];
    this.moq = moq || 0;
    this.deliveryWindow = deliveryWindow;
    this.pickupWindow = pickupWindow;
    this.totalQuantity = 0;

    if (item) {
      this.addItem(item);
    }
  }

  addItem(item: CartItem | CartItem[]) {
    if (Array.isArray(item)) {
      item.forEach((singleItem) => this.addItem(singleItem));
      return;
    }

    const existingIndex = this.items.findIndex((existingItem) => existingItem.skuId === item.skuId);

    if (existingIndex > -1) {
      this.items[existingIndex].quantity += item.quantity;
    } else {
      this.items.push(item);
    }

    this.totalQuantity += item.quantity;
  }

  static fromObject(obj: Cart, supplier?: Supplier): CartImpl {
    const items: CartItem[] = obj.items.map(CartItemImpl.fromObject);

    const deliveryWindow =
      obj.deliveryWindow !== undefined
        ? DeliveryWindowImpl.fromObject(obj.deliveryWindow)
        : undefined;

    const pickupWindow =
      obj.pickupWindow !== undefined ? PickupWindowImpl.fromObject(obj.pickupWindow) : undefined;

    return new CartImpl(
      obj.supplierId,
      items,
      supplier?.moq || obj.moq,
      deliveryWindow,
      pickupWindow,
    );
  }

  withItems(item: CartItem | CartItem[]): Cart {
    return new CartImpl(this.supplierId, item, this.moq, this.deliveryWindow, this.pickupWindow);
  }
}
